import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AdvertisementCarousel from '../components/AdvertisementCarousel';
import AdvertisementVideo from '../components/AdvertisementVideo';
import SearchResults from '../components/SearchResults';
import ProductCard from '../components/ProductCard';
import { productService } from '../services/productService';
import homeImage from '../assets/maxfly-home.png';

const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    productService.getProducts({ featured: true, max: 8 }).then((res) => {
      if (res.success) setFeaturedProducts(res.products);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Si c'est un lien URL, rediriger directement
      if (isUrl(searchQuery.trim())) {
        navigate('/order', { state: { productLink: searchQuery.trim() } });
      } else {
        // Sinon, naviguer vers les catégories avec le terme de recherche
        navigate('/categories', { state: { search: searchQuery.trim() } });
      }
      setShowResults(false);
    }
  };

  const isUrl = (str) => {
    try {
      const url = new URL(str.startsWith('http') ? str : `https://${str}`);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Afficher les résultats si au moins 2 caractères
    setShowResults(value.trim().length >= 2);
  };

  const handleInputFocus = () => {
    if (searchQuery.trim().length >= 2) {
      setShowResults(true);
    }
  };

  return (
    <div>
      <Navbar />

      {/* Hero section with search bar - Alibaba style */}
      <div className="hero-section" style={{ backgroundImage: `url(${homeImage})` }}>
        <div className="hero-overlay">
          <div className="search-container">
            <div className="welcome-text">
              <h1 className="welcome-title">
                Bienvenue sur Maxfly
              </h1>
              <p className="welcome-subtitle">
                Achetez en local en RDC ou commandez depuis Shein, Amazon, Alibaba et d&apos;autres plateformes internationales.
              </p>
            </div>
            
            <div className="search-box" ref={searchContainerRef} style={{ position: 'relative' }}>
              <form onSubmit={handleSearch} style={{ display: 'flex', width: '100%' }}>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Rechercher un produit, un lien, une catégorie..."
                  value={searchQuery}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                />
                <button type="submit" className="search-button">
                  Rechercher
                </button>
              </form>
              {showResults && (
                <SearchResults
                  query={searchQuery}
                  onClose={() => setShowResults(false)}
                  onSelect={() => setShowResults(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Zone publicitaire 1 - Carrousel */}
      <div className="container" style={{ marginTop: '30px', marginBottom: '30px' }}>
        <AdvertisementCarousel position="carousel" autoPlay={true} interval={5000} />
      </div>

      {/* Boutique locale */}
      <div className="container" style={{ marginTop: 30, marginBottom: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 24, margin: 0, fontWeight: 600 }}>Boutique locale 🇨🇩</h2>
          <Link to="/shop" className="btn btn-primary">
            Voir toute la boutique
          </Link>
        </div>
        {featuredProducts.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 16,
            }}
          >
            {featuredProducts.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        ) : (
          <div className="card" style={{ padding: 32, textAlign: 'center' }}>
            <p style={{ marginBottom: 12 }}>Produits locaux bientôt disponibles ici.</p>
            <Link to="/shop">Parcourir la boutique</Link>
          </div>
        )}
      </div>

      {/* Categories section - Alibaba style */}
      <div className="categories-section">
        <div className="container">
          <h2 style={{ 
            fontSize: '24px', 
            marginBottom: '30px', 
            color: '#333', 
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Catégories pour vous
          </h2>
          <div className="categories-grid">
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200&h=200&fit=crop" 
                  alt="Mode & Accessoires"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '👗'; }}
                />
              </div>
              <div className="category-name">Mode & Accessoires</div>
            </div>
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1498049794561-7780e7231661?w=200&h=200&fit=crop" 
                  alt="Électronique"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '📱'; }}
                />
              </div>
              <div className="category-name">Électronique</div>
            </div>
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=200&fit=crop" 
                  alt="Maison & Jardin"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '🏠'; }}
                />
              </div>
              <div className="category-name">Maison & Jardin</div>
            </div>
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1522338247332-321f44d1be92?w=200&h=200&fit=crop" 
                  alt="Beauté"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '💄'; }}
                />
              </div>
              <div className="category-name">Beauté</div>
            </div>
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=200&h=200&fit=crop" 
                  alt="Chaussures"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '👟'; }}
                />
              </div>
              <div className="category-name">Chaussures</div>
            </div>
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=200&h=200&fit=crop" 
                  alt="Sports & Loisirs"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '🎮'; }}
                />
              </div>
              <div className="category-name">Sports & Loisirs</div>
            </div>
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=200&h=200&fit=crop" 
                  alt="Bijoux & Montres"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '💍'; }}
                />
              </div>
              <div className="category-name">Bijoux & Montres</div>
            </div>
            <div className="category-item" onClick={() => navigate('/categories')}>
              <div className="category-icon">
                <img 
                  src="https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=200&h=200&fit=crop" 
                  alt="Enfants & Jouets"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '🧸'; }}
                />
              </div>
              <div className="category-name">Enfants & Jouets</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="features-section">
        <div className="container">
          <div className="features-header">
            <h2 className="features-title">
              Pourquoi choisir <span className="features-title-accent">Maxfly</span> ?
            </h2>
            <p className="features-subtitle">
              La solution e-commerce qui révolutionne vos achats internationaux
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.2"/>
                </svg>
              </div>
              <h3 className="feature-title">Accès Mondial Simplifié</h3>
              <p className="feature-description">
                Explorez des millions de produits depuis Shein, Amazon, Alibaba et bien plus encore. Une seule plateforme pour tous vos besoins.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" fill="currentColor"/>
                  <path d="M12 11h4v2h-4v-2zm0-4h4v2h-4V7z" fill="white"/>
                </svg>
              </div>
              <h3 className="feature-title">Sécurité Totale</h3>
              <p className="feature-description">
                Vos transactions sont protégées par des systèmes de paiement sécurisés. Vos données personnelles restent privées et confidentielles.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className="feature-title">Suivi Intelligent</h3>
              <p className="feature-description">
                Suivez chaque étape de votre commande en temps réel. De la validation à la livraison, restez informé à chaque instant.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h17c1.1 0 2-.9 2-2v-7c0-1.1-.9-2-2-2zM8 14H5v-2h3v2zm0-3H5V9h3v2zm0-3H5V6h3v2zm6 6h-4v-2h4v2zm0-3h-4V9h4v2zm0-3h-4V6h4v2zm7 6h-4v-2h4v2zm0-3h-4V9h4v2zm0-3h-4V6h4v2z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className="feature-title">Livraison Express</h3>
              <p className="feature-description">
                Recevez vos commandes rapidement et en toute sécurité jusqu'en RDC. Un réseau de livraison fiable à votre service.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="cta-section">
            <h3 className="cta-title">
              Prêt à révolutionner vos achats ?
            </h3>
            <p className="cta-subtitle">
              Rejoignez des milliers de clients satisfaits et découvrez une nouvelle façon de shopper
            </p>
            <div className="cta-buttons">
              <button
                className="btn btn-primary cta-button-primary"
                onClick={() => navigate('/shop')}
              >
                Boutique locale
              </button>
              <button
                className="btn btn-secondary cta-button-secondary"
                onClick={() => navigate('/order')}
              >
                Commande internationale
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Zone publicitaire 2 - Vidéo (juste avant le footer) */}
      <AdvertisementVideo position="banner" />

      <Footer />
    </div>
  );
};

export default Home;

